import { getTranslation } from "../../lib/translations";
import { convertToElement, SelectorOrElement } from "../../lib/elementUtils";

export class LoadingSpinner {
    private spinner?: HTMLElement;
    private parent: HTMLElement;
    private isWaitingToLoad = false;
    message?: string;

    /**
     * @param parent The parent element to cover with an overlay. Defaults to the entire document
     * @param message The message to display with the loading spinner
     */
    constructor(
        parent: SelectorOrElement = document.body,
        message?: string
    ) {
        let parentElement = convertToElement<HTMLElement>(parent);
        if (!parentElement) {
            console.error("Could not find parent element for spinner: ", parent);
            parentElement = document.body;
        }
        this.parent = parentElement;

        this.message = message ?? "";

        // Allow spinner to be positioned absolutely to the parent
        if (getComputedStyle(this.parent)["position"] === "static") {
            this.parent.style.position = "relative";
        }
    }

    /**
     * Create a new loading spinner that is ready to be added to the DOM
     *
     * @returns The loading spinner element
     */
    private createSpinner = (): HTMLElement => {
        const spinner = document.createElement("div");
        spinner.classList.add("loading-spinner");
        spinner.innerHTML = /*html*/ `
        <div class="loading-spinner__spinner">
            <span aria-hidden="true" class='material-icons'>cached</span>
            <span aria-live="assertive" role="alert">${this.message}</span>
        </div>
        `;
        return spinner;
    };

    /** Display the loading spinner to the user */
    startSpinner = async () => {
        this.isWaitingToLoad = true;

        if (this.spinner) {
            this.stopSpinner();
        }

        this.spinner = this.createSpinner();
        this.parent.appendChild(this.spinner);
    };

    /** Stop the loading spinner and remove it from the DOM */
    stopSpinner = () => {
        this.isWaitingToLoad = false;
        if (this.spinner) {
            try {
                this.parent.removeChild(this.spinner);
            } catch {
                // removeChild may fail if the node has already been removed. If the operation fails because of this, the task has already completed
            }
            this.spinner = undefined;
        }
    };

    /**
     * Load the default loading message similair to "Loading" in the current language
     *
     * This should be used when an explicit loading message has not already been set in the constructor
     * */
    loadDefaultTranslationText = async () => {
        try {
            const translation = await getTranslation("Loading");
            this.message = translation;
        } catch {
            console.error("Failed to get translated loading text. Using default fallback message");
            this.message = "Loading"
        }
    }

}
